import { useEffect, useCallback, useRef, useMemo } from 'react';
import { apiCallStatus } from 'helpers/constants';

/**
 * Performance-optimized custom hook for automatic powertrain expansion and asset selection
 * @param {string} searchBoxFilter - The current search term
 * @param {Array} powertrainOverviewList - List of powertrains with their assets
 * @param {number|null} selectedPowertrain - Currently selected/expanded powertrain ID
 * @param {function} setSelectedPowertrain - Function to set selected powertrain
 * @param {function} handleSelectedItem - Function to handle asset selection
 * @param {object} selectedAsset - Currently selected asset
 * @param {string} [sensorIdentifierFilter] - The current Smart Sensor Identifier filter value
 * @param {string} [loadingStatus] - Loading status of powertrainOverviewList, used to avoid
 * auto-selecting from a stale list while a new sensor identifier request is in flight
 */
export const useSearchExpansion = (
    searchBoxFilter,
    powertrainOverviewList,
    selectedPowertrain,
    setSelectedPowertrain,
    handleSelectedItem,
    selectedAsset,
    sensorIdentifierFilter,
    loadingStatus
) => {
    const previousSearchRef = useRef('');
    const previousSensorIdentifierRef = useRef('');
    const searchTimeoutRef = useRef(null);
    const expansionTimeoutRef = useRef(null);

    // Memoize flattened asset list for better performance with large datasets
    const flattenedAssets = useMemo(() => {
        if (!Array.isArray(powertrainOverviewList)) {
            return [];
        }

        return powertrainOverviewList.reduce((acc, powertrain) => {
            if (powertrain?.assets?.length > 0) {
                for (const asset of powertrain.assets) {
                    acc.push({
                        ...asset,
                        powertrainId: powertrain.powertrainId,
                        powertrainName: powertrain.powertrainName
                    });
                }
            }
            return acc;
        }, []);
    }, [powertrainOverviewList]);

    /**
     * Find matching asset using optimized search
     * @param {string} searchTerm - Search term to match against
     * @returns {object|null} - Matching asset with powertrain info or null
     */
    const findMatchingAsset = useCallback((searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            return null;
        }

        const searchLower = searchTerm.toLowerCase().trim();

        // Use Array.find for early termination - more efficient for large lists
        return flattenedAssets.find(asset => {
            const assetName = asset?.assetName?.toLowerCase() || '';
            const assetId = asset?.assetId?.toString() || '';
            const assetSerialNumber = asset?.assetSerialNumber?.toLowerCase() || '';
            return assetName.includes(searchLower) || assetId.includes(searchLower) || assetSerialNumber.includes(searchLower);
        });
    }, [flattenedAssets]);

    /**
     * Trigger powertrain expansion by clicking the UI element
     * @param {number} powertrainId - ID of powertrain to expand
     */
    const triggerPowertrainExpansion = useCallback((powertrainId) => {
        // Clear any existing expansion timeout
        if (expansionTimeoutRef.current) {
            clearTimeout(expansionTimeoutRef.current);
        }

        expansionTimeoutRef.current = setTimeout(() => {
            const powertrainElement = document.querySelector(`[data-powertrain-id="${powertrainId}"]`);
            if (!powertrainElement) {
                return;
            }

            // Optimized selector for clickable elements
            const clickableElement = powertrainElement.querySelector(
                'button, [role="button"], .expandable-title, .expandable-header'
            );

            if (clickableElement) {
                const rect = clickableElement.getBoundingClientRect();
                if (rect.width > 0 && rect.height > 0) {
                    clickableElement.click();
                }
            }
        }, 50); // Reduced timeout for faster response
    }, []);

    /**
     * Main search and expansion logic
     */
    const findAndExpandAsset = useCallback((searchTerm) => {
        // Skip if search term hasn't changed or is too short
        if (!searchTerm || searchTerm.trim().length < 2 || searchTerm === previousSearchRef.current) {
            return;
        }

        const searchLower = searchTerm.toLowerCase().trim();
        previousSearchRef.current = searchTerm;

        // Find matching asset using optimized search
        const matchingAsset = findMatchingAsset(searchLower);

        if (!matchingAsset) {
            return;
        }

        // Clear any existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Expand powertrain if not already expanded
        if (selectedPowertrain !== matchingAsset.powertrainId) {
            triggerPowertrainExpansion(matchingAsset.powertrainId);
        }

        // Select the matching asset
        if (selectedAsset?.assetId !== matchingAsset.assetId) {
            searchTimeoutRef.current = setTimeout(() => {
                handleSelectedItem(matchingAsset);
            }, 400); // Optimized delay
        }
    }, [findMatchingAsset, selectedPowertrain, selectedAsset, triggerPowertrainExpansion, handleSelectedItem]);    // Clear previous search when search term becomes empty
    useEffect(() => {
        if (!searchBoxFilter || searchBoxFilter.trim() === '') {
            previousSearchRef.current = '';
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = null;
            }
            if (expansionTimeoutRef.current) {
                clearTimeout(expansionTimeoutRef.current);
                expansionTimeoutRef.current = null;
            }
        }
    }, [searchBoxFilter]);

    // Debounced search execution with optimized delay
    useEffect(() => {
        if (searchBoxFilter && searchBoxFilter.trim().length >= 2) {
            // Reduced debounce time for better responsiveness
            const timeoutId = setTimeout(() => {
                findAndExpandAsset(searchBoxFilter);
            }, 200);

            return () => clearTimeout(timeoutId);
        }
    }, [searchBoxFilter, findAndExpandAsset]);

    // The Smart Sensor Identifier filter is resolved server-side, so there is no matching field to
    // search by here — auto-expand/select only once it has narrowed the list to a single asset.
    useEffect(() => {
        const sensorIdentifierTerm = sensorIdentifierFilter?.trim();

        if (!sensorIdentifierTerm) {
            previousSensorIdentifierRef.current = '';
            return;
        }

        if (searchBoxFilter && searchBoxFilter.trim().length > 0) {
            return;
        }

        // Wait for the debounced request to settle so we don't act on a stale (pre-filter) list.
        if (loadingStatus === apiCallStatus.LOADING) {
            return;
        }

        if (sensorIdentifierTerm === previousSensorIdentifierRef.current || flattenedAssets.length !== 1) {
            return;
        }

        previousSensorIdentifierRef.current = sensorIdentifierTerm;
        const [matchingAsset] = flattenedAssets;

        if (selectedPowertrain !== matchingAsset.powertrainId) {
            triggerPowertrainExpansion(matchingAsset.powertrainId);
        }

        if (selectedAsset?.assetId !== matchingAsset.assetId) {
            handleSelectedItem(matchingAsset);
        }
    }, [sensorIdentifierFilter, searchBoxFilter, loadingStatus, flattenedAssets, selectedPowertrain, selectedAsset, triggerPowertrainExpansion, handleSelectedItem]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (expansionTimeoutRef.current) {
                clearTimeout(expansionTimeoutRef.current);
            }
        };
    }, []);

    return null; // This hook doesn't render anything
};
